import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCode, FaMobileAlt, FaDraftingCompass, FaRocket, FaShieldAlt, FaChartLine } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './ServiceLanding.css';

const serviceData = {
    'web-development-vadodara': {
        title: 'Web Development Company in Vadodara',
        subtitle: 'High-Performance Web Solutions',
        description: 'We build scalable, secure, and lightning-fast websites and web applications tailored to your business needs in Vadodara and beyond.',
        features: [
            { icon: <FaCode />, title: 'Custom Web Apps', desc: 'Tailor-made solutions using React, Node.js, and modern tech stacks.' },
            { icon: <FaRocket />, title: 'Performance First', desc: 'optimized for speed, SEO, and user experience.' },
            { icon: <FaShieldAlt />, title: 'Secure & Scalable', desc: 'Enterprise-grade security and architecture that grows with you.' }
        ],
        metaTitle: 'Web Development Company in Vadodara | Nimai Solutions',
        metaDescription: 'Looking for a web development company in Vadodara? Nimai Solutions offers premium website design and development services using modern technologies.'
    },
    'flutter-app-development': {
        title: 'Flutter App Development Services',
        subtitle: 'Cross-Platform Excellence',
        description: 'Leverage the power of Flutter to build beautiful, natively compiled applications for mobile, web, and desktop from a single codebase.',
        features: [
            { icon: <FaMobileAlt />, title: 'Single Codebase', desc: 'Reach iOS and Android users simultaneously with high-quality apps.' },
            { icon: <FaRocket />, title: 'Fast Development', desc: 'Quick turnaround times with Hot Reload and pre-built widgets.' },
            { icon: <FaChartLine />, title: 'Native Performance', desc: 'Smooth animations and high performance that feels truly native.' }
        ],
        metaTitle: 'Flutter App Development Services | Cross-Platform Apps',
        metaDescription: 'Expert Flutter app development services to build high-performance, beautiful cross-platform mobile applications for iOS and Android.'
    },
    'mobile-app-development': {
        title: 'Mobile App Development Company',
        subtitle: 'Innovative Mobile Solutions',
        description: 'From concept to deployment, we create engaging mobile experiences that connect your brand with users on their favorite devices.',
        features: [
            { icon: <FaMobileAlt />, title: 'iOS & Android', desc: 'Deep expertise in both major platforms for maximum reach.' },
            { icon: <FaDraftingCompass />, title: 'User-Centric UI', desc: 'Interfaces designed for intuitive navigation and high engagement.' },
            { icon: <FaRocket />, title: 'App Store Success', desc: 'Complete support for App Store and Play Store optimization.' }
        ],
        metaTitle: 'Mobile App Development Company | iOS & Android Apps',
        metaDescription: 'Professional mobile app development services. We build custom iOS and Android applications that drive business growth.'
    },
    'ui-ux-design-services': {
        title: 'UI/UX Design Services in Vadodara',
        subtitle: 'Human-Centric Design',
        description: 'We create visually stunning and highly functional designs that prioritize user experience and business outcomes.',
        features: [
            { icon: <FaDraftingCompass />, title: 'Interface Design', desc: 'Modern, clean, and brand-aligned visual interfaces.' },
            { icon: <FaChartLine />, title: 'Experience Strategy', desc: 'Data-driven design decisions based on user behavior and goals.' },
            { icon: <FaRocket />, title: 'Prototyping', desc: 'Interactive prototypes to visualize and test the flow before development.' }
        ],
        metaTitle: 'UI/UX Design Services in Vadodara | User Experience Design',
        metaDescription: 'Premium UI/UX design services in Vadodara. We create intuitive and engaging digital experiences that users love.'
    }
};

const ServiceLanding = () => {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const data = serviceData[serviceId];

    useEffect(() => {
        if (!data) {
            navigate('/');
        } else {
            document.title = data.metaTitle;
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) {
                metaDesc.setAttribute('content', data.metaDescription);
            }
            window.scrollTo(0, 0);
        }
    }, [data, navigate]);

    if (!data) return null;

    return (
        <div className="service-landing-container">
            <Navbar />

            <section className="service-landing-hero">
                <motion.span
                    className="service-badge"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {data.subtitle}
                </motion.span>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    {data.title.split(' ').map((word, i) => (
                        <span key={i} className={i >= data.title.split(' ').length - 2 ? 'highlight' : ''}>
                            {word}{' '}
                        </span>
                    ))}
                </motion.h1>
                <motion.p
                    className="service-landing-desc"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    {data.description}
                </motion.p>
                <motion.button
                    className="btn-primary"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    onClick={() => navigate('/contact')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Get a Free Audit
                </motion.button>
            </section>

            <section className="service-features-section">
                <div className="features-grid">
                    {data.features.map((feature, index) => (
                        <motion.div
                            key={index}
                            className="feature-card"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <div className="feature-icon">{feature.icon}</div>
                            <h3>{feature.title}</h3>
                            <p>{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section className="service-cta">
                <div className="cta-box">
                    <h2>Ready to Scale Your Business?</h2>
                    <p style={{ color: '#A0AEC0', marginBottom: '30px' }}>
                        Let's build something amazing together. Our team is ready to help you achieve your digital goals.
                    </p>
                    <button className="btn-primary" onClick={() => navigate('/contact')}>
                        Contact Us Today
                    </button>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServiceLanding;
